import React, { useState } from "react";
import { useLang } from "../LangContext";
import { useReveal } from "../hooks/useReveal";
import { Kicker } from "./Mission";
import { toast } from "sonner";
import { Bell, Mail, Send, Users, Clock, Star } from "lucide-react";
import { FOLLOW_ME_CARDS, BOX_RECTO_URL, BOX_VERSO_URL } from "../assets/brand";

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const STAT_ICONS = [Users, Clock, Star];

export const FollowMe = () => {
  const { t } = useLang();
  const ref = useReveal();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  // NEWSLETTER INTEGRATION POINT
  // Replace the body of this handler with your real newsletter service
  // (Mailchimp / Brevo / ConvertKit / custom POST /api/newsletter etc.).
  // Right now this is a 100% fake bloc — just shows a toast confirmation.
  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      toast.error(t.followMe.newsletter.invalid);
      return;
    }
    setSending(true);
    // TODO: wire up real provider here
    await new Promise((r) => setTimeout(r, 650));
    setSending(false);
    setEmail("");
    toast.success(t.followMe.newsletter.success);
  };

  return (
    <section
      id="follow-me"
      data-testid="followme-section"
      className="relative py-24 lg:py-36 overflow-hidden reveal"
      ref={ref}
    >
      {/* Section accent backdrop */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(700px_500px_at_80%_20%,rgba(217,70,239,0.28),transparent_60%),radial-gradient(700px_500px_at_15%_80%,rgba(255,107,0,0.25),transparent_60%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left — text & newsletter */}
          <div className="lg:col-span-6">
            <Kicker label={t.followMe.kicker} />
            <h2
              className="font-display mt-4 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.95]"
              data-testid="followme-title"
            >
              <span className="block text-baj-grad">{t.followMe.title1}</span>
              <span className="block text-white">{t.followMe.title2}</span>
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-xl">
              {t.followMe.text}
            </p>

            {/* Quick stats */}
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              {t.followMe.stats.map((s, i) => {
                const Icon = STAT_ICONS[i];
                return (
                  <div
                    key={s.label}
                    className="glass rounded-2xl px-3 py-4 text-center"
                  >
                    <div className="flex justify-center mb-2">
                      <Icon className="w-4 h-4 text-fuchsia-300" strokeWidth={2.5} />
                    </div>
                    <div className="font-display text-2xl font-extrabold text-white">
                      {s.value}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-1">
                      {s.label}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Newsletter — placeholder block */}
            <div
              className="mt-10 glass-strong rounded-3xl p-6 sm:p-7 relative overflow-hidden"
              data-testid="newsletter-block"
            >
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-baj-grad opacity-30 blur-3xl" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 text-xs uppercase font-bold tracking-[0.25em] text-fuchsia-200/90">
                  <Bell className="w-3.5 h-3.5" />
                  {t.followMe.kicker}
                </div>
                <h3 className="font-display mt-3 text-2xl sm:text-3xl font-extrabold text-white">
                  {t.followMe.newsletter.title}
                </h3>
                <p className="mt-2 text-slate-300 text-sm sm:text-base">
                  {t.followMe.newsletter.sub}
                </p>

                <form
                  onSubmit={handleSubscribe}
                  className="mt-5 flex flex-col sm:flex-row gap-3"
                  data-testid="newsletter-form"
                >
                  <div className="relative flex-1">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      inputMode="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.followMe.newsletter.placeholder}
                      className="w-full bg-white/5 border border-white/15 rounded-full pl-11 pr-4 py-3.5 text-white placeholder:text-slate-400 focus:outline-none focus:border-fuchsia-400/60 focus:bg-white/10 transition"
                      data-testid="newsletter-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="btn-primary justify-center disabled:opacity-60 disabled:cursor-wait"
                    data-testid="newsletter-submit-button"
                  >
                    {sending ? "..." : t.followMe.newsletter.button}
                    <Send className="w-4 h-4" />
                  </button>
                </form>

                <p className="mt-3 text-xs text-slate-400">{t.followMe.note}</p>
              </div>
            </div>
          </div>

          {/* Right — visual: real Follow Me cards as a fan */}
          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] sm:aspect-square w-full max-w-xl mx-auto">
              {/* Glow halo */}
              <div className="absolute inset-10 rounded-[3rem] bg-baj-grad blur-3xl opacity-50 spin-slow" />
              {/* Card fan */}
              <div className="relative h-full w-full flex items-center justify-center">
                {FOLLOW_ME_CARDS.slice(0, 5).map((card, i) => {
                  const positions = [
                    { rot: -18, x: -130, y: 30, z: 1, scale: 0.78 },
                    { rot: -9, x: -65, y: 8, z: 2, scale: 0.88 },
                    { rot: 0, x: 0, y: -8, z: 5, scale: 1 },
                    { rot: 9, x: 65, y: 8, z: 2, scale: 0.88 },
                    { rot: 18, x: 130, y: 30, z: 1, scale: 0.78 },
                  ];
                  const p = positions[i];
                  return (
                    <div
                      key={card.url}
                      data-testid={`followme-card-${i}`}
                      className="absolute"
                      style={{
                        transform: `translate(${p.x}px, ${p.y}px) rotate(${p.rot}deg) scale(${p.scale})`,
                        zIndex: p.z,
                      }}
                    >
                      <div
                        className="float-slower"
                        style={{
                          animationDelay: `${i * 0.4}s`,
                          "--r": "0deg",
                        }}
                      >
                        <div className="w-40 sm:w-44 aspect-[3/4] rounded-2xl overflow-hidden bg-white shadow-[0_25px_50px_-10px_rgba(217,70,239,0.55)] ring-1 ring-white/30 hover:scale-105 transition-transform duration-500">
                          <img
                            src={card.url}
                            alt={card.label}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Badge */}
              <div className="absolute top-2 left-2 inline-flex items-center gap-1.5 rounded-full bg-black/65 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-white border border-white/15 z-10">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
                Bientôt sur Ulule
              </div>
            </div>

            <div className="mt-8 flex justify-center">
              <a
                href="#follow-me"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector("[data-testid='newsletter-input']")?.focus();
                }}
                className="btn-ghost"
                data-testid="followme-notify-btn"
              >
                <Bell className="w-4 h-4" />
                {t.followMe.ctaNotify}
              </a>
            </div>
          </div>
        </div>

        {/* Cards gallery strip */}
        <div className="mt-20 lg:mt-28" data-testid="followme-cards-gallery">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <Kicker label={t.followMe.gallery.kicker} />
              <h3 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                {t.followMe.gallery.title1}{" "}
                <span className="text-baj-grad">{t.followMe.gallery.title2}</span>
              </h3>
            </div>
            <p className="text-sm text-slate-400 max-w-sm">
              {t.followMe.gallery.sub}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {FOLLOW_ME_CARDS.map((card, i) => (
              <div
                key={card.url}
                className="group relative rounded-2xl overflow-hidden bg-white ring-1 ring-white/15 shadow-[0_10px_30px_-10px_rgba(217,70,239,0.4)] hover:-translate-y-1.5 hover:shadow-[0_20px_45px_-10px_rgba(217,70,239,0.6)] transition-all duration-500"
                data-testid={`gallery-card-${i}`}
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={card.url}
                    alt={card.label}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-orange-300">
                    {card.type}
                  </div>
                  <div className="text-sm font-bold text-white mt-0.5">
                    {card.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
